<template>
  <div class="loginPage">
    <v-row dense no-gutters class="auth-row">
      <v-col cols="12" sm="12" md="6" class="custom-height">
        <div class="auth-light-version">
          <div class="logo">
            <v-img :width="logoWidth" :height="logoHeight" :src="logoSrc"></v-img>
          </div>
          <h3>Log in</h3>
          <v-form class="deposit-form password-field">
            <div class="form-details">
              <fieldset>
                <legend>Email</legend>
                <v-text-field
                  v-model="email"
                  placholder="Enter your email"
                  single-line
                  solo
                  hide-details
                  class="custom-select"
                  type="email"
                  :disabled="isLoading"
                ></v-text-field>
              </fieldset>
              <fieldset>
                <legend>Password</legend>
                <v-text-field
                  placholder="Enter Here"
                  v-model="password"
                  :append-icon="show1 ? 'mdi-eye' : 'mdi-eye-off'"
                  :type="show1 ? 'text' : 'password'"
                  counter
                  @click:append="show1 = !show1"
                  single-line
                  solo
                  hide-details
                  class="custom-select"
                  :disabled="isLoading"
                ></v-text-field>
              </fieldset>
              <v-btn
                class="ma-0 mt-5 text-capitalize btn-account"
                color="greenMain"
                tile
                block
                elevation="0"
                @click.prevent="handleLogin"
                :loading="isLoading"
                :disabled="isLoading"
              >
                {{ isLoading ? "Logging in..." : "Log in" }}
              </v-btn>
            </div>
          </v-form>
        </div>
      </v-col>
      <v-col cols="12" sm="12" md="6" class="image-col">
        <div class="image-content">
          <h3 class="mb-2">Contact us at</h3>
          <p>
            <a :href="'mailto:' + loginpageinfo?.company_email">{{
              loginpageinfo?.company_email
            }}</a>
            <small> if you have problems</small>
          </p>
          <social-icons :info="genInfo"></social-icons>
        </div>
        <div class="image-container">
          <v-img src="@/assets/auth.png" class="zoom-animate-full image-full"></v-img>
        </div>
      </v-col>
    </v-row>
    <!-- Active Session Dialog -->
    <v-dialog v-model="showActiveSessionDialog" max-width="450" class="rounded-0">
      <v-card
        rounded="0"
        width="450"
        class="auth-modal-card"
        :color="$vuetify.theme.dark ? '#111A26' : '#ffffff'"
      >
        <v-card-title class="px-0 pt-0">Already logged in elsewhere.</v-card-title>
        <v-card-text class="px-0 pb-0">
          <p>Would you like to log out and continue logging in on this device?</p>
        </v-card-text>
        <v-card-actions class="px-0 pb-0">
          <v-spacer></v-spacer>
          <v-btn
            class="elevation-0"
            :color="$vuetify.theme.dark ? '#FF567F' : '#FF567F'"
            @click="forceLogoutAndContinue"
            :loading="isForceLoggingOut"
          >
            Login here
          </v-btn>
          <v-btn
            class="elevation-0"
            :color="$vuetify.theme.dark ? '#111A26' : '#ffffff'"
            outlined
            @click="cancelLogin"
            >Cancel</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { isValidEmail } from "@/_helpers/validate";
import LanguageMenu from "@/components/TopBarAndSideBar/LanguageMenu.vue";
import toast from "@/services/Notification";
import { AUTHENTICATION__ACTIONS, COMPANYINFO__ACTIONS } from "@/store/action-types";
import { mapActions, mapState } from "vuex";
import SocialIcons from "./SocialIcons.vue";

export default {
  components: { LanguageMenu, SocialIcons },
  data() {
    return {
      genInfo: {},
      currentRouteName: "",
      show1: false,
      email: "",
      password: "",
      isLoading: false,
      showActiveSessionDialog: false,
      isForceLoggingOut: false,
    };
  },
  computed: {
    ...mapState("authentication", [
      "loginInfo",
      "viewPassword",
      "alreadyLoggedIn",
      "existingDevice",
    ]),
    ...mapState("company", ["loginpageinfo"]),
    themeColor() {
      return this.$vuetify.theme.dark ? "#162334" : "#E8F1F8";
    },

    logoWidth() {
      return this.loginpageinfo?.logo_width || "100px";
    },

    logoHeight() {
      return this.loginpageinfo?.logo_height || "auto";
    },

    logoSrc() {
      return this.$vuetify.theme.dark
        ? this.loginpageinfo?.logo_url
        : this.loginpageinfo?.logo_url_dark;
    },

    companyName() {
      return this.loginpageinfo?.company_name || "";
    },
  },

  methods: {
    ...mapActions("company", [COMPANYINFO__ACTIONS.LOGINPAGEINFO]),
    ...mapActions("authentication", [
      AUTHENTICATION__ACTIONS.LOGIN,
      AUTHENTICATION__ACTIONS.FORCELOGOUT,
    ]),

    async fetchCompanyInfo() {
      try {
        await this[COMPANYINFO__ACTIONS.LOGINPAGEINFO]();
      } catch (error) {
        console.error("Failed to fetch company info:", error);
      }
    },

    async handleLogin() {
      const parameter = {
        email: this.email,
        password: this.password,
      };

      if (!isValidEmail(this.email)) {
        toast.error("Invalid Email Format");
        return;
      }

      try {
        this.isLoading = true;
        await this[AUTHENTICATION__ACTIONS.LOGIN](parameter);
        if (this.alreadyLoggedIn) {
          this.showActiveSessionDialog = true;
        }
      } catch (error) {
        toast.error("Login failed. Please try again.");
      } finally {
        this.isLoading = false;
      }
    },

    async forceLogoutAndContinue() {
      try {
        this.isForceLoggingOut = true;

        const result = await this[AUTHENTICATION__ACTIONS.FORCELOGOUT]();

        if (result.success) {
          // Add a small delay to ensure database updates propagate
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // After successful force logout, attempt login with forceLogout:true
          const loginResult = await this[AUTHENTICATION__ACTIONS.LOGIN]({
            email: this.email,
            password: this.password,
            forceLogout: true,
          });

          this.showActiveSessionDialog = false;

          if (!loginResult.success) {
            toast.error("Login failed after force logout. Please try again.");
          }
        } else {
          toast.error(result.message || "Failed to logout other session");
        }
      } catch (error) {
        console.error("Force logout and continue error:", error);
      } finally {
        this.isForceLoggingOut = false;
      }
    },

    cancelLogin() {
      this.showActiveSessionDialog = false;
    },
  },

  async created() {
    await this.fetchCompanyInfo();
    this.currentRouteName = this.$route.name;
  },
};
</script>
